import React, { useEffect, useState, useCallback } from 'react';
import { useWallet } from '../hooks/useWallet';

const REST = 'https://rest.amitis.network';
const RPC  = 'https://rpc.amitis.network';

// All 21 petal slots — known earnings addresses for live validators
// Slots 8-21 will be populated as validators come online
const PETAL_MAP = {
  1:  { earnings: 'amitis1mzzpffa8lzqwh0zry6yj57t7qzu43v3m88n5as', moniker: 'amitis-validator-1', valoper: 'amitisvaloper14uxdwplhhznclgaa350945tkyyw7e5e2f5sam5' },
  2:  { earnings: 'amitis125uguxqeyg5g5ut9av63d4r6y68h0q3f2ccxs6', moniker: 'amitis-validator-2', valoper: 'amitisvaloper1f4wrlrjzyxeavzz9n5dfgx4d4n0tsg8ysv7u4s' },
  3:  { earnings: 'amitis17hzth0glx85vem7d3mf25nv7xluhvhmpvashdk', moniker: 'amitis-validator-3', valoper: 'amitisvaloper14fmvjd4jmp3adgr97zqfnw3asgdr0efd0ctkw2' },
  4:  { earnings: 'amitis1y3kw3vmm9y5u7r5dtvu039547v59f5d99hjuhf', moniker: 'amitis-validator-4', valoper: 'amitisvaloper1sy2rqgl6ulvgtgp33qeeupvvunn8700lqcj8u9' },
  5:  { earnings: 'amitis1qgmv64lv2hrs9zp6svcx79dtk2h0uq89uejyuz', moniker: 'amitis-validator-5', valoper: 'amitisvaloper12qywa3pksm9k2y6g70zsjatw6mejxfp2xvh33p' },
  6:  { earnings: 'amitis19x2j0xvehycq8s2cqznzazkgdcc9kh8rpxp76r', moniker: 'amitis-validator-6', valoper: 'amitisvaloper1srn92hlpgvwfy6h9yfs2h3ujaenh3fmg75t7u7' },
  7:  { earnings: 'amitis1zjr4as6p95dapyvsn9qes9m8yv39fawnrvwthm', moniker: 'amitis-validator-7', valoper: 'amitisvaloper187l63xnptfhufjyuk9snj5panr9tunmm7anu4q' },
  // Petals 8-21 — coming online soon
  8:  null, 9:  null, 10: null, 11: null, 12: null, 13: null, 14: null,
  15: null, 16: null, 17: null, 18: null, 19: null, 20: null, 21: null,
};

const AMTS_PRICE = 7.50;

export default function Validators() {
  const { address } = useWallet();
  const [validators, setValidators]   = useState([]);
  const [earnings, setEarnings]       = useState({});
  const [outstanding, setOutstanding] = useState({});
  const [pool, setPool]               = useState('—');
  const [loading, setLoading]         = useState(true);
  const [withdrawing, setWithdrawing] = useState(null);
  const [withdrawResult, setWithdrawResult] = useState(null);
  const [networkStats, setNetworkStats] = useState({});

  // Which petal does the connected wallet match?
  const myPetal = address
    ? Object.entries(PETAL_MAP).find(([, v]) => v && v.earnings === address)?.[0]
    : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Load validators, community pool, and network stats in parallel
      const [vRes, pRes, statusRes] = await Promise.all([
        fetch(REST + '/cosmos/staking/v1beta1/validators?status=BOND_STATUS_BONDED&pagination.limit=25').then(r => r.json()),
        fetch(REST + '/cosmos/distribution/v1beta1/community_pool').then(r => r.json()),
        fetch(RPC + '/status').then(r => r.json()),
      ]);

      setValidators(vRes.validators || []);
      const u = (pRes.pool || []).find(c => c.denom === 'uamts');
      if (u) setPool((parseFloat(u.amount) / 1e6).toLocaleString(undefined, { maximumFractionDigits: 0 }));

      const height = statusRes.result?.sync_info?.latest_block_height;
      setNetworkStats({ height });

      // Load earnings balances and outstanding rewards for all live petals
      const liveEntries = Object.entries(PETAL_MAP).filter(([, v]) => v);
      const [earnResults, outstandingResults] = await Promise.all([
        Promise.all(liveEntries.map(([petal, info]) =>
          fetch(REST + '/cosmos/bank/v1beta1/balances/' + info.earnings)
            .then(r => r.json())
            .then(d => ({ petal, balances: d.balances || [] }))
            .catch(() => ({ petal, balances: [] }))
        )),
        Promise.all(liveEntries.map(([petal, info]) =>
          fetch(REST + '/cosmos/distribution/v1beta1/validators/' + info.valoper + '/outstanding_rewards')
            .then(r => r.json())
            .then(d => ({ petal, rewards: d.rewards?.rewards || [] }))
            .catch(() => ({ petal, rewards: [] }))
        )),
      ]);

      const newEarnings = {};
      earnResults.forEach(({ petal, balances }) => {
        const amts = balances.find(b => b.denom === 'uamts');
        newEarnings[petal] = amts ? parseFloat(amts.amount) / 1e6 : 0;
      });
      setEarnings(newEarnings);

      const newOutstanding = {};
      outstandingResults.forEach(({ petal, rewards }) => {
        const amts = rewards.find(r => r.denom === 'uamts');
        newOutstanding[petal] = amts ? parseFloat(amts.amount) / 1e6 : 0;
      });
      setOutstanding(newOutstanding);

    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  async function withdrawRewards(petal) {
    if (!window.keplr) {
      setWithdrawResult({ ok: false, msg: 'Keplr wallet required to withdraw rewards' });
      return;
    }
    const info = PETAL_MAP[petal];
    if (!info) return;
    setWithdrawing(petal);
    setWithdrawResult(null);
    try {
      await window.keplr.enable('amitis-network');
      const offlineSigner = window.keplr.getOfflineSigner('amitis-network');
      const { SigningStargateClient } = await import('@cosmjs/stargate');
      const client = await SigningStargateClient.connectWithSigner(
        'https://rpc.amitis.network', offlineSigner
      );
      const accounts = await offlineSigner.getAccounts();
      const sender = accounts[0].address;
      const msg = {
        typeUrl: '/cosmos.distribution.v1beta1.MsgWithdrawValidatorCommission',
        value: { validatorAddress: info.valoper },
      };
      const fee = { amount: [{ denom: 'uamts', amount: '150000' }], gas: '250000' };
      const result = await client.signAndBroadcast(sender, [msg], fee);
      if (result.code === 0) {
        setWithdrawResult({ ok: true, msg: 'Rewards withdrawn successfully', hash: result.transactionHash });
        setTimeout(load, 3000);
      } else {
        setWithdrawResult({ ok: false, msg: result.rawLog || 'Withdrawal failed' });
      }
    } catch(e) {
      setWithdrawResult({ ok: false, msg: e.message || 'Withdrawal failed' });
    }
    setWithdrawing(null);
  }

  const totalEarnings = Object.values(earnings).reduce((a, b) => a + b, 0);
  const totalOutstanding = Object.values(outstanding).reduce((a, b) => a + b, 0);
  const activeCount = validators.length;
  const mono = { fontFamily: 'DM Mono, monospace' };

  return (
    <div className="page-content fade-up">

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <span className="section-label">Cooperative Infrastructure</span>
          <div style={{ fontSize: 22, fontWeight: 800 }}>Validator Petals</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className="badge badge-green">{activeCount} / 21 Active</span>
          {myPetal && (
            <span className="badge badge-gold" style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}>
              Your Petal: #{myPetal}
            </span>
          )}
        </div>
      </div>

      {/* Network summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Earnings (Wallets)', val: totalEarnings.toLocaleString(undefined,{maximumFractionDigits:2}) + ' AMTS', sub: '$' + (totalEarnings * AMTS_PRICE).toLocaleString(undefined,{maximumFractionDigits:0}), color: 'var(--green)' },
          { label: 'Unclaimed Rewards', val: totalOutstanding.toLocaleString(undefined,{maximumFractionDigits:2}) + ' AMTS', sub: '$' + (totalOutstanding * AMTS_PRICE).toLocaleString(undefined,{maximumFractionDigits:0}), color: 'var(--gold, #fbbf24)' },
          { label: 'Community Pool', val: pool + ' AMTS', sub: 'governance controlled', color: 'var(--blue, #4d9fff)' },
          { label: 'Per Petal (Outstanding)', val: (totalOutstanding / Math.max(activeCount,1)).toLocaleString(undefined,{maximumFractionDigits:2}) + ' AMTS', sub: '$' + ((totalOutstanding / Math.max(activeCount,1)) * AMTS_PRICE).toLocaleString(undefined,{maximumFractionDigits:0}), color: 'var(--green)' },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '16px 18px' }}>
            <div style={{ ...mono, fontSize: 9, color: 'var(--dimmer)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>{s.label}</div>
            <div style={{ ...mono, fontSize: 16, fontWeight: 700, color: s.color, marginBottom: 2 }}>{s.val}</div>
            <div style={{ ...mono, fontSize: 10, color: 'var(--muted)' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* My petal highlight — only shown when connected as a validator */}
      {myPetal && PETAL_MAP[myPetal] && (
        <div style={{
          background: 'rgba(251,191,36,0.06)',
          border: '1px solid rgba(251,191,36,0.25)',
          borderRadius: 12,
          padding: '20px 24px',
          marginBottom: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div style={{ ...mono, fontSize: 9, color: '#fbbf24', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Your Validator — Petal #{myPetal}</div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{PETAL_MAP[myPetal].moniker}</div>
            </div>
            <button
              className="btn btn-primary"
              style={{ background: '#fbbf24', color: '#000', fontWeight: 700 }}
              onClick={() => withdrawRewards(myPetal)}
              disabled={withdrawing === myPetal || outstanding[myPetal] < 0.01}
            >
              {withdrawing === myPetal
                ? <><div className="spinner"></div> Withdrawing…</>
                : `Withdraw ${outstanding[myPetal]?.toFixed(4) || '0'} AMTS`}
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            {[
              { label: 'Earnings Wallet', val: (earnings[myPetal] || 0).toLocaleString(undefined,{maximumFractionDigits:4}) + ' AMTS' },
              { label: 'Unclaimed Rewards', val: (outstanding[myPetal] || 0).toLocaleString(undefined,{maximumFractionDigits:4}) + ' AMTS' },
              { label: 'USD Value (Outstanding)', val: '$' + ((outstanding[myPetal] || 0) * AMTS_PRICE).toLocaleString(undefined,{maximumFractionDigits:2}) },
            ].map(s => (
              <div key={s.label}>
                <div style={{ ...mono, fontSize: 9, color: 'var(--dimmer)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>{s.label}</div>
                <div style={{ ...mono, fontSize: 14, fontWeight: 700, color: '#fbbf24' }}>{s.val}</div>
              </div>
            ))}
          </div>
          {withdrawResult && (
            <div style={{
              marginTop: 12, padding: '10px 14px', borderRadius: 8,
              background: withdrawResult.ok ? 'var(--green-dim)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${withdrawResult.ok ? 'var(--green-border)' : 'rgba(239,68,68,0.25)'}`,
              color: withdrawResult.ok ? 'var(--green)' : '#ef4444',
              ...mono, fontSize: 11,
            }}>
              {withdrawResult.msg}
              {withdrawResult.hash && <div style={{ opacity: 0.7, marginTop: 4 }}>TX: {withdrawResult.hash.slice(0,20)}…</div>}
            </div>
          )}
        </div>
      )}

      {/* Not connected as validator — show connect hint */}
      {address && !myPetal && (
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: '14px 18px',
          marginBottom: 20,
          ...mono, fontSize: 12, color: 'var(--muted)',
        }}>
          Connected wallet is not a validator earnings address. Connect your validator earnings wallet to see your personalized dashboard.
        </div>
      )}

      {!address && (
        <div style={{
          background: 'var(--green-dim)',
          border: '1px solid var(--green-border)',
          borderRadius: 10,
          padding: '14px 18px',
          marginBottom: 20,
          ...mono, fontSize: 12, color: 'var(--green)',
        }}>
          Validator owner? Connect your earnings wallet to see your personalized dashboard and withdraw rewards.
        </div>
      )}

      {/* All 21 petals grid */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ ...mono, fontSize: 9, color: 'var(--dimmer)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 12 }}>All 21 Validator Petals</div>

        {/* Active validators */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '50px 1fr 160px 160px 140px 100px', gap: 12, padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'rgba(255,255,255,0.02)' }}>
            {['Petal','Validator','Earnings Wallet','Unclaimed Rewards','USD Value','Status'].map(h => (
              <div key={h} style={{ ...mono, fontSize: 9, color: 'var(--dimmer)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div className="loading-row"><div className="spinner"></div>Loading validator data…</div>
          ) : (
            Array.from({length: 21}, (_, i) => i + 1).map(petal => {
              const info = PETAL_MAP[petal];
              const isLive = !!info;
              const isMe = String(petal) === String(myPetal);
              const earn = earnings[petal] || 0;
              const out = outstanding[petal] || 0;
              const validator = info ? validators.find(v => v.operator_address === info.valoper) : null;

              return (
                <div
                  key={petal}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '50px 1fr 160px 160px 140px 100px',
                    gap: 12,
                    padding: '13px 18px',
                    borderBottom: '1px solid var(--border)',
                    background: isMe ? 'rgba(251,191,36,0.04)' : '',
                    borderLeft: isMe ? '2px solid #fbbf24' : '2px solid transparent',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { if (!isMe) e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                  onMouseLeave={e => { if (!isMe) e.currentTarget.style.background = ''; }}
                >
                  {/* Petal number */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: isLive ? 'var(--green-dim)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${isLive ? 'var(--green-border)' : 'var(--border)'}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      ...mono, fontSize: 10, fontWeight: 700,
                      color: isLive ? 'var(--green)' : 'var(--dimmer)',
                    }}>
                      {petal}
                    </div>
                  </div>

                  {/* Moniker */}
                  <div>
                    {isLive ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                          {validator?.description?.moniker || info.moniker}
                          {isMe && <span style={{ ...mono, fontSize: 9, color: '#fbbf24', marginLeft: 8, background: 'rgba(251,191,36,0.12)', padding: '2px 8px', borderRadius: 3 }}>YOU</span>}
                        </div>
                        <div style={{ ...mono, fontSize: 10, color: 'var(--dimmer)' }}>{info.earnings.slice(0,14)}…</div>
                      </>
                    ) : (
                      <div style={{ ...mono, fontSize: 11, color: 'var(--dimmer)' }}>Coming soon</div>
                    )}
                  </div>

                  {/* Earnings wallet balance */}
                  <div style={{ ...mono, fontSize: 12, color: isLive ? 'var(--white)' : 'var(--dimmer)', alignSelf: 'center' }}>
                    {isLive ? earn.toLocaleString(undefined, {maximumFractionDigits: 4}) + ' AMTS' : '—'}
                  </div>

                  {/* Outstanding rewards */}
                  <div style={{ ...mono, fontSize: 12, color: isLive ? 'var(--green)' : 'var(--dimmer)', alignSelf: 'center' }}>
                    {isLive ? out.toLocaleString(undefined, {maximumFractionDigits: 4}) + ' AMTS' : '—'}
                  </div>

                  {/* USD value */}
                  <div style={{ ...mono, fontSize: 12, color: isLive ? 'var(--muted)' : 'var(--dimmer)', alignSelf: 'center' }}>
                    {isLive ? '$' + (out * AMTS_PRICE).toLocaleString(undefined, {maximumFractionDigits: 2}) : '—'}
                  </div>

                  {/* Status */}
                  <div style={{ alignSelf: 'center' }}>
                    {isLive ? (
                      <span className="badge badge-green" style={{ fontSize: 9 }}>Active</span>
                    ) : (
                      <span style={{ ...mono, fontSize: 9, color: 'var(--dimmer)', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', padding: '2px 8px', borderRadius: 3 }}>Pending</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* How it works */}
      <div className="card">
        <div style={{ ...mono, fontSize: 9, color: 'var(--green)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 14 }}>How Validator Revenue Works</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 20 }}>
          {[
            { title: 'Transaction Fees', body: 'Every transaction on Amitis pays a 0.3% fee. After the 80% user rebate, the remaining fees flow to the distribution module and are split equally across all 21 active validators.' },
            { title: 'Flash Loan Fees', body: 'The sAMTS flash loan facility charges 0.05% per loan on 21M AMTS pool. These fees are distributed equally across all 21 validator petals every block.' },
            { title: 'Withdrawing Rewards', body: 'Validators accumulate rewards in the distribution module. Connect your earnings wallet to withdraw directly to your wallet. Rewards are denominated in AMTS.' },
          ].map(s => (
            <div key={s.title}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.65 }}>{s.body}</div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
